/**
 * ToolParameterEditor — Rich visual editor for a tool's input parameter
 * schema (PR — sysadmin Tools feature expansion, Phase 1).
 *
 * Extracted from the tenant create page (`studio/tools.new.tsx` rows
 * ~566-673) so the sysadmin catalog wizard
 * (`sysadmin/catalog.tools.new.tsx`) can reuse the same UX rather than
 * forcing sysadmin authors to hand-write JSON Schema in a textarea.
 *
 * Contract:
 *   - Pure controlled component (parameters + onChange).
 *   - Emits the same UI-side `ToolParameter` shape both surfaces already
 *     persist. Serialisation to JSON Schema happens in the caller via
 *     `buildJsonSchemaFromParameters` below (also exported).
 *   - Per-row blank-name validation matches the tenant page's existing
 *     STUDIO-R6-002 behaviour: inline error + red border + ARIA hookup.
 *
 * No data fetching, no toast, no router — caller wires those.
 */

import { Plus, X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

// ─── Types ──────────────────────────────────────────────────────────────────

export type ToolParameterType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'array'
  | 'object';

/**
 * UI-side parameter shape. Both consumers store the serialized form as
 * a JSON Schema object (see `buildJsonSchemaFromParameters`), but the
 * UI manipulates this richer struct so users get type pickers, required
 * toggles, and inline descriptions without writing JSON.
 */
export interface ToolParameter {
  name: string;
  type: ToolParameterType;
  required: boolean;
  description: string;
}

export const TOOL_PARAMETER_TYPES: readonly ToolParameterType[] = [
  'string',
  'number',
  'boolean',
  'array',
  'object',
] as const;

// ─── Serialisation helpers ──────────────────────────────────────────────────

/**
 * Build a JSON Schema fragment from the editor's parameter array.
 * Matches the on-disk shape both `workspace_tools.spec` (tenant) and
 * `system_tools.input_schema` (sysadmin) use:
 *   { type: 'object', properties: { name: { type, description } }, required: [] }
 *
 * Blank names are dropped on serialisation — the caller should already
 * be enforcing non-empty names via `someParameterHasBlankName` before
 * calling Create, but defensive output keeps the persisted schema
 * sound even if a bug slips through.
 */
export function buildJsonSchemaFromParameters(
  parameters: ToolParameter[],
): Record<string, unknown> {
  const properties: Record<
    string,
    { type: string; description: string }
  > = {};
  const required: string[] = [];
  for (const p of parameters) {
    const name = p.name.trim();
    if (!name) continue;
    properties[name] = {
      type: p.type,
      description: p.description.trim() || `Parameter ${name}`,
    };
    if (p.required) required.push(name);
  }
  return { type: 'object', properties, required };
}

/**
 * Inverse of `buildJsonSchemaFromParameters`. Used when the wizard
 * (sysadmin) opens an existing tool for edit and needs to hydrate the
 * rich editor from a stored JSON Schema. Best-effort: anything that
 * doesn't look like the canonical
 *   { type: 'object', properties: {...}, required: [] }
 * shape returns an empty array — the caller is expected to surface
 * "schema not editor-compatible" UX (fall back to JSON textarea).
 */
export function parseParametersFromJsonSchema(
  schema: unknown,
): ToolParameter[] {
  if (
    !schema ||
    typeof schema !== 'object' ||
    Array.isArray(schema) ||
    (schema as { type?: unknown }).type !== 'object'
  ) {
    return [];
  }
  const props = (schema as { properties?: unknown }).properties;
  if (!props || typeof props !== 'object' || Array.isArray(props)) {
    return [];
  }
  const requiredRaw = (schema as { required?: unknown }).required;
  const required = Array.isArray(requiredRaw)
    ? new Set(requiredRaw.filter((v): v is string => typeof v === 'string'))
    : new Set<string>();
  const out: ToolParameter[] = [];
  for (const [name, valRaw] of Object.entries(props)) {
    if (!valRaw || typeof valRaw !== 'object' || Array.isArray(valRaw)) {
      continue;
    }
    const val = valRaw as { type?: unknown; description?: unknown };
    const type = (TOOL_PARAMETER_TYPES as readonly string[]).includes(
      val.type as string,
    )
      ? (val.type as ToolParameterType)
      : 'string';
    out.push({
      name,
      type,
      required: required.has(name),
      description: typeof val.description === 'string' ? val.description : '',
    });
  }
  return out;
}

/**
 * Returns true if any parameter has a blank name. The caller should
 * gate Save on this; the row UI separately surfaces an inline error.
 */
export function someParameterHasBlankName(parameters: ToolParameter[]): boolean {
  return parameters.some((p) => !p.name.trim());
}

// ─── Component ──────────────────────────────────────────────────────────────

interface ToolParameterEditorProps {
  parameters: ToolParameter[];
  onChange: (next: ToolParameter[]) => void;
  /** Optional description override for the empty state. */
  emptyHint?: string;
  /** Optional minimum count gate copy. */
  requireAtLeastOne?: boolean;
}

export function ToolParameterEditor({
  parameters,
  onChange,
  emptyHint,
  requireAtLeastOne = true,
}: ToolParameterEditorProps) {
  const addParameter = () => {
    onChange([
      ...parameters,
      { name: '', type: 'string', required: false, description: '' },
    ]);
  };

  const updateParameter = (
    index: number,
    field: keyof ToolParameter,
    value: string | boolean,
  ) => {
    onChange(
      parameters.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
    );
  };

  const removeParameter = (index: number) => {
    onChange(parameters.filter((_, i) => i !== index));
  };

  if (parameters.length === 0) {
    return (
      <div className="border border-dashed rounded-lg p-6 text-center">
        <p className="text-sm text-muted-foreground">No parameters defined</p>
        <p className="text-xs text-muted-foreground/70 mt-1">
          {emptyHint ??
            (requireAtLeastOne
              ? 'Add at least one parameter for agents to pass when calling this tool.'
              : 'Add parameters to declare what this tool accepts.')}
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3 gap-1"
          onClick={addParameter}
          data-testid="tool-param-add-empty"
        >
          <Plus className="h-3 w-3" /> Add Parameter
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Parameters</Label>
        <Button
          variant="outline"
          size="sm"
          className="gap-1 h-7 text-xs"
          onClick={addParameter}
          data-testid="tool-param-add"
        >
          <Plus className="h-3 w-3" /> Add Parameter
        </Button>
      </div>
      {parameters.map((param, i) => {
        const blankParamName = !param.name.trim();
        return (
          <div
            key={i}
            className={cn(
              'p-3 rounded-lg border space-y-2',
              blankParamName && 'border-destructive/40',
            )}
            data-testid={`tool-param-row-${i}`}
          >
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Input
                  placeholder="parameter_name"
                  value={param.name}
                  onChange={(e) =>
                    updateParameter(
                      i,
                      'name',
                      e.target.value.replace(/[^a-zA-Z0-9_]/g, ''),
                    )
                  }
                  className={cn(
                    'font-mono text-xs h-8',
                    blankParamName &&
                      'border-destructive focus-visible:ring-destructive',
                  )}
                  aria-invalid={blankParamName || undefined}
                  aria-label={`Name for parameter ${i + 1}`}
                  aria-describedby={
                    blankParamName ? `tool-param-name-error-${i}` : undefined
                  }
                  data-testid={`tool-param-name-${i}`}
                />
                {blankParamName && (
                  <p
                    id={`tool-param-name-error-${i}`}
                    className="text-[10px] text-destructive flex items-start gap-1 mt-1"
                  >
                    <AlertCircle className="h-2.5 w-2.5 mt-0.5 shrink-0" />
                    <span>Name is required.</span>
                  </p>
                )}
              </div>
              <Select
                value={param.type}
                onValueChange={(v) => updateParameter(i, 'type', v)}
              >
                <SelectTrigger
                  className="w-28 h-8 text-xs"
                  aria-label={
                    param.name
                      ? `Type for parameter ${param.name}`
                      : `Type for parameter ${i + 1}`
                  }
                  data-testid={`tool-param-type-${i}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TOOL_PARAMETER_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1.5">
                <Switch
                  checked={param.required}
                  onCheckedChange={(v) => updateParameter(i, 'required', v)}
                  className="scale-75"
                  data-testid={`tool-param-required-${i}`}
                />
                <span className="text-[10px] text-muted-foreground">
                  Required
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => removeParameter(i)}
                aria-label="Remove parameter"
                title="Remove parameter"
                data-testid={`tool-param-remove-${i}`}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
            <Input
              placeholder="Description of this parameter"
              value={param.description}
              onChange={(e) =>
                updateParameter(i, 'description', e.target.value)
              }
              className="text-xs h-8"
              aria-label={
                param.name
                  ? `Description for parameter ${param.name}`
                  : `Description for parameter ${i + 1}`
              }
              data-testid={`tool-param-desc-${i}`}
            />
          </div>
        );
      })}
    </div>
  );
}
